import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faXmark, faInbox, faBuilding } from '@fortawesome/free-solid-svg-icons';
import CgRadixButton from 'modules/Common/ui/CgRadixButton';
import CgCnGoodHoverText from 'modules/Common/entities/CgCnGoodHoverText';
import { useBranding } from 'modules/App/hooks/useBranding';
import { cgToast } from 'modules/Common/ui/toast';
import { useFetchCnGoodsCbam } from 'modules/Common/hooks/useFetchCnGoodsCbam';
import { useSupplierInbox, useSupplierInboxActions } from 'modules/FreeSupplier/useSupplierInbox';
import { useSupplierFactorActions } from 'modules/FreeSupplier/useSupplierFactors';
import {
  SupplierConnectionState,
  SupplierDataRequestState,
} from 'modules/FreeSupplier/supplierInboxContracts';
import {
  canDeclineRequest,
  canTerminateConnection,
  getConnectionPrimaryCtas,
  getRequestCombinedAcceptCta,
  getRequestPrimaryCta,
} from 'modules/FreeSupplier/supplierInboxPresentation';
import {
  buildRequestEntryQueue,
  requestEntryCompletionMessage,
  requestEntryProgressLabel,
  resolveRequestLineEntrySeed,
} from 'modules/FreeSupplier/supplierRequestEntrySeed';
import useCnGoodLabel from '../state/useCnGoodLabel';
import { useMockData } from '../state/MockDataContext';
import SupplierEmissionsModal, { SupplierSaveMode } from '../components/SupplierEmissionsModal';
import { SupplierCardListSkeleton } from '../components/SupplierLoadingStates';

// Supplier-facing labels for the connection lifecycle — the declarant
// relationship, distinct from the individual data requests inside it.
// Builders (not static maps) so every label routes through i18n — called
// once per render via useMemo in the components below, same pattern as
// Common/GapStatusPill.jsx's buildTypeLabels.
const buildConnectionLabel = (t) => ({
  [SupplierConnectionState.Pending]: t(
    'freeSupplier:dataRequests.connectionState.pending',
    'Connection requested',
  ),
  [SupplierConnectionState.Accepted]: t(
    'freeSupplier:dataRequests.connectionState.accepted',
    'Connected',
  ),
  [SupplierConnectionState.Declined]: t(
    'freeSupplier:dataRequests.connectionState.declined',
    'Declined',
  ),
  [SupplierConnectionState.Expired]: t(
    'freeSupplier:dataRequests.connectionState.expired',
    'Expired',
  ),
  [SupplierConnectionState.Cancelled]: t(
    'freeSupplier:dataRequests.connectionState.cancelled',
    'Cancelled',
  ),
  [SupplierConnectionState.Terminated]: t(
    'freeSupplier:dataRequests.connectionState.terminated',
    'Disconnected',
  ),
});

const CONNECTION_TONE = {
  [SupplierConnectionState.Pending]: 'text-amber-700 bg-amber-50',
  [SupplierConnectionState.Accepted]: 'text-emerald-700 bg-emerald-50',
};

// One line of copy per connection state so the supplier understands the
// relationship they are consenting to — reviewed independently per declarant.
const buildConnectionCopy = (t) => ({
  [SupplierConnectionState.Pending]: t(
    'freeSupplier:dataRequests.connectionCopy.pending',
    'Review this declarant relationship independently from every other customer.',
  ),
  [SupplierConnectionState.Accepted]: t(
    'freeSupplier:dataRequests.connectionCopy.accepted',
    'This declarant can receive the data you publish for their matching requests.',
  ),
});

const buildRequestLabel = (t) => ({
  [SupplierDataRequestState.Pending]: t(
    'freeSupplier:dataRequests.requestState.pending',
    'Data requested',
  ),
  [SupplierDataRequestState.Accepted]: t(
    'freeSupplier:dataRequests.requestState.accepted',
    'Accepted',
  ),
  [SupplierDataRequestState.InProgress]: t(
    'freeSupplier:dataRequests.requestState.inProgress',
    'In progress',
  ),
  [SupplierDataRequestState.PartiallyFulfilled]: t(
    'freeSupplier:dataRequests.requestState.partiallyFulfilled',
    'Partially fulfilled',
  ),
  [SupplierDataRequestState.Fulfilled]: t(
    'freeSupplier:dataRequests.requestState.fulfilled',
    'Fulfilled',
  ),
  [SupplierDataRequestState.Declined]: t(
    'freeSupplier:dataRequests.requestState.declined',
    'Declined',
  ),
  [SupplierDataRequestState.Cancelled]: t(
    'freeSupplier:dataRequests.requestState.cancelled',
    'Cancelled',
  ),
});

const REQUEST_TONE = {
  [SupplierDataRequestState.Pending]: 'text-amber-700 bg-amber-50',
  [SupplierDataRequestState.Accepted]: 'text-emerald-700 bg-emerald-50',
  [SupplierDataRequestState.InProgress]: 'text-emerald-700 bg-emerald-50',
  [SupplierDataRequestState.PartiallyFulfilled]: 'text-emerald-700 bg-emerald-50',
  [SupplierDataRequestState.Fulfilled]: 'text-emerald-700 bg-emerald-50',
};

const badgeTone = (map, state) => map[state] || 'text-slate-500 bg-slate-100';

const formatDate = (value) =>
  new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(value));

// A single data request inside a connection panel: the goods + reporting period
// a declarant needs, its state, and the fastest action to satisfy it. Each
// request is accepted/declined on its own — there is no accept-all.
function RequestRow({ connection, request, actions, busy, onProvideData }) {
  const { t } = useTranslation('freeSupplier');
  const combinedAccept = getRequestCombinedAcceptCta(connection, request, t);
  const requestCta = getRequestPrimaryCta(request.nextAction, t);
  const requestLabel = useMemo(() => buildRequestLabel(t), [t]);
  const cnCodes = useMemo(
    () => [...new Set(request.lines.map((line) => line.cnCode))],
    [request.lines],
  );

  // A still-pending connection accepts the connection and the request together
  // (per-relationship consent); an already-accepted connection just accepts or
  // fulfils the request.
  let primary = null;
  if (combinedAccept) {
    primary = {
      label: combinedAccept.label,
      onClick: () =>
        actions.acceptConnectionAndRequest.mutate({
          connectionId: connection.id,
          requestId: request.id,
        }),
    };
  } else if (requestCta?.action === 'accept_request') {
    primary = { label: requestCta.label, onClick: () => actions.acceptRequest.mutate(request.id) };
  } else if (requestCta?.action === 'provide_data') {
    primary = {
      label: requestCta.label,
      onClick: () => onProvideData(connection, request),
    };
  }

  return (
    <div className="flex flex-col gap-3 px-5 py-4">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0 flex flex-wrap items-center gap-1.5">
          {cnCodes.map((code) => (
            <span
              key={code}
              className="text-[11px] text-slate-600 bg-slate-100 rounded-full px-2 py-0.5"
            >
              <CgCnGoodHoverText
                cnCode={code}
                useAutonomousData
                variant="plain"
                triggerClassName="!text-[11px] !font-normal"
              />
            </span>
          ))}
          <span className="text-[11px] text-slate-500 border border-slate-200 rounded-full px-2 py-0.5">
            {request.reportingPeriod}
          </span>
        </div>
        <span
          className={`text-[11px] font-semibold rounded-full px-2 py-0.5 shrink-0 ${badgeTone(
            REQUEST_TONE,
            request.state,
          )}`}
        >
          {requestLabel[request.state] || request.state}
        </span>
      </div>

      {primary || canDeclineRequest(request.state) ? (
        <div className="flex items-center gap-2">
          {primary ? (
            <CgRadixButton
              variant="primary"
              size="sm"
              icon={faCheck}
              disabled={busy}
              onClick={primary.onClick}
            >
              {primary.label}
            </CgRadixButton>
          ) : null}
          {canDeclineRequest(request.state) ? (
            <CgRadixButton
              variant="ghost"
              size="sm"
              icon={faXmark}
              disabled={busy}
              onClick={() => actions.declineRequest.mutate(request.id)}
            >
              {t('freeSupplier:dataRequests.decline', 'Decline')}
            </CgRadixButton>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// One panel per connection (declarant relationship). Two declarants produce two
// independent panels; a declarant can open several requests on one connection,
// each actionable on its own row. Connection-level controls (accept/decline the
// relationship while pending, disconnect once accepted) live in the header.
function ConnectionPanel({ connection, actions, busy, onProvideData }) {
  const { t } = useTranslation('freeSupplier');
  const connectionCtas = getConnectionPrimaryCtas(connection, t);
  const isPending = connection.state === SupplierConnectionState.Pending;
  const connectionLabel = useMemo(() => buildConnectionLabel(t), [t]);
  const connectionCopy = useMemo(() => buildConnectionCopy(t), [t]);
  const copy = connectionCopy[connection.state];

  return (
    <section className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
      <div className="px-5 py-4 bg-slate-50/60 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center shrink-0">
            <FontAwesomeIcon
              icon={faBuilding}
              className="text-slate-400"
            />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-800 m-0">{connection.declarant.name}</p>
            <p className="text-xs text-slate-400 m-0 mt-0.5">
              {t('freeSupplier:dataRequests.connectionSince', 'Connection · {{date}}', {
                date: formatDate(connection.createdAt),
              })}
            </p>
          </div>
          <span
            className={`text-[11px] font-semibold rounded-full px-2 py-0.5 shrink-0 ${badgeTone(
              CONNECTION_TONE,
              connection.state,
            )}`}
          >
            {connectionLabel[connection.state] || connection.state}
          </span>
        </div>

        {copy ? <p className="text-xs text-slate-500 m-0 mt-2">{copy}</p> : null}

        {isPending && connectionCtas.length > 0 ? (
          <div className="flex items-center gap-2 mt-3">
            <CgRadixButton
              variant="primary"
              size="sm"
              icon={faCheck}
              disabled={busy}
              onClick={() => actions.acceptConnection.mutate(connection.id)}
            >
              {t('freeSupplier:dataRequests.acceptConnection', 'Accept connection')}
            </CgRadixButton>
            <CgRadixButton
              variant="ghost"
              size="sm"
              icon={faXmark}
              disabled={busy}
              onClick={() => actions.declineConnection.mutate(connection.id)}
            >
              {t('freeSupplier:dataRequests.decline', 'Decline')}
            </CgRadixButton>
          </div>
        ) : null}

        {canTerminateConnection(connection.state) ? (
          <div className="mt-3">
            <CgRadixButton
              variant="ghost"
              size="sm"
              icon={faXmark}
              disabled={busy}
              onClick={() => actions.terminateConnection.mutate(connection.id)}
            >
              {t('freeSupplier:dataRequests.terminate', 'Terminate')}
            </CgRadixButton>
          </div>
        ) : null}
      </div>

      <div className="divide-y divide-slate-100">
        {connection.requests.length === 0 ? (
          <p className="px-5 py-4 text-xs text-slate-400 m-0">
            {t(
              'freeSupplier:dataRequests.noRequestsOnConnection',
              'No data requests on this connection yet.',
            )}
          </p>
        ) : (
          connection.requests.map((request) => (
            <RequestRow
              key={request.id}
              connection={connection}
              request={request}
              actions={actions}
              busy={busy}
              onProvideData={onProvideData}
            />
          ))
        )}
      </div>
    </section>
  );
}

// Item B: a request line the "provide data" walk still owes data for, paired
// with its position/counters. `queue`/`position` drive which line is seeded
// next; publishedCount/draftCount/skippedCount feed the honest completion
// summary (requestEntryCompletionMessage) once the whole queue is walked —
// see supplierRequestEntrySeed.js for the shared pure logic. Item 20: the
// target installation is no longer tracked here at all — it's owned entirely
// by SupplierEmissionsModal's own `installationChoice` selection, which
// starts empty on every mount (see that component) — there is nothing left
// for this flow state to resolve or remember between lines.
const initialProvideDataFlow = (connection, request) => {
  const queue = buildRequestEntryQueue(request.lines);
  if (queue.length === 0) return null;
  return {
    connectionId: connection.id,
    requestId: request.id,
    declarantName: connection.declarant.name,
    queue,
    position: 0,
    publishedCount: 0,
    draftCount: 0,
    skippedCount: 0,
  };
};

// Incoming declarant connections + their data requests, backed by the S3
// supplier inbox (/supplier/inbox). Outgoing precursor-supplier requests are a
// separate O2O concern and are not part of this surface.
export default function DataRequestsPage() {
  const { t } = useTranslation('freeSupplier');
  const brand = useBranding();
  const inbox = useSupplierInbox();
  const actions = useSupplierInboxActions();
  const { operator, installationDocs } = useMockData();
  const cnGoodsQuery = useFetchCnGoodsCbam();
  const cnGoods = cnGoodsQuery.data ?? [];
  const cnGoodLabel = useCnGoodLabel();
  const { invalidate } = useSupplierFactorActions();
  const connections = inbox.data?.items || [];

  const busy = [
    actions.acceptConnectionAndRequest,
    actions.acceptConnection,
    actions.acceptRequest,
    actions.declineConnection,
    actions.declineRequest,
    actions.terminateConnection,
  ].some((mutation) => mutation.isPending);

  // Item 20: "Provide data" opens SupplierEmissionsModal IMMEDIATELY, in
  // place on this page (no navigation, no intermediate card) — seeded from
  // the request's first unfulfilled line, auto-advancing through every
  // remaining line on each successful save.
  const [provideDataFlow, setProvideDataFlow] = useState(null);
  const onProvideData = (connection, request) =>
    setProvideDataFlow(initialProvideDataFlow(connection, request));

  const currentEntry = provideDataFlow ? provideDataFlow.queue[provideDataFlow.position] : null;
  const currentLine = currentEntry?.line || null;
  const { seed, needsCnGoodPick } = useMemo(
    () => resolveRequestLineEntrySeed(currentLine, cnGoods),
    [currentLine, cnGoods],
  );
  const cnGoodHint =
    currentLine && needsCnGoodPick
      ? t(
          'freeSupplier:dataRequests.cnGoodHint',
          'Requested: {{cnGood}} — pick the specific good you produce.',
          { cnGood: cnGoodLabel(currentLine.cnCode) },
        )
      : undefined;
  const installations = installationDocs || [];

  const total = provideDataFlow?.queue.length || 0;
  const progressLabel = provideDataFlow
    ? requestEntryProgressLabel(provideDataFlow.position, total)
    : '';
  // Item 20 locked copy: "Value N of M — {declarant}'s data request", with
  // the CN-good hint (chapter/heading-level requests the catalog can't
  // safely auto-match, Q3) appended when there is one.
  const contextNotice = provideDataFlow
    ? [
        t(
          'freeSupplier:dataRequests.valueProgress',
          'Value {{progress}} — {{declarantName}}’s data request',
          {
            progress: progressLabel,
            declarantName: provideDataFlow.declarantName,
          },
        ),
        cnGoodHint,
      ]
        .filter(Boolean)
        .join(' — ')
    : undefined;

  // Root cause (item 20): resolveRequestLineEntrySeed only overrides
  // seed.cnGood with the exact catalog match once the CN catalog has
  // actually loaded, and SupplierEmissionsModal deliberately syncs its
  // selectedEmission seed into the shared emission store ONLY ONCE, on
  // mount (a legitimate guard against wiping in-progress input on parent
  // re-renders). So the modal must not mount before the catalog query has
  // SUCCEEDED — mounting earlier would freeze an unresolved seed, and
  // remounting on readiness would discard anything already entered (codex
  // review P2). Readiness is the query's real state, not `length > 0`:
  // loading shows the overlay below, an error shows a retry path, and a
  // legitimately-empty catalog still counts as ready (the seed resolver
  // degrades gracefully; chapter/heading-level codes stay unselected with
  // the hint above by design, see Q3).
  const cnGoodsReady = cnGoodsQuery.isSuccess;

  // Shared by a successful save (mode: 'draft' | 'publish') and an explicit
  // Skip (mode: 'skip') — both move the walk forward by one; only the FINAL
  // step invalidates the inbox/factors lists and shows the honest completion
  // summary (item B/3 — a drafted or skipped line was never "provided").
  const advanceProvideDataFlow = async (mode) => {
    if (!provideDataFlow) return;
    const counts = {
      publishedCount: provideDataFlow.publishedCount + (mode === 'publish' ? 1 : 0),
      draftCount: provideDataFlow.draftCount + (mode === 'draft' ? 1 : 0),
      skippedCount: provideDataFlow.skippedCount + (mode === 'skip' ? 1 : 0),
    };
    const nextPosition = provideDataFlow.position + 1;
    if (nextPosition >= provideDataFlow.queue.length) {
      await invalidate();
      cgToast.success(t('freeSupplier:dataRequests.completionToastTitle', 'Data requests'), {
        description: requestEntryCompletionMessage({
          total: provideDataFlow.queue.length,
          ...counts,
        }),
      });
      setProvideDataFlow(null);
    } else {
      setProvideDataFlow({
        ...provideDataFlow,
        ...counts,
        position: nextPosition,
      });
    }
  };

  // Item B/4: closing mid-sequence never loses already-saved lines (each save
  // is committed individually) — just stop the walk.
  const closeProvideDataFlow = () => {
    if (!provideDataFlow) return;
    setProvideDataFlow(null);
    invalidate();
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-800 m-0">
            {t('freeSupplier:dataRequests.title', 'Data Requests')}
          </h1>
          <p className="text-sm text-slate-500 mt-1 m-0">
            {t(
              'freeSupplier:dataRequests.subtitle',
              'Declarants connect and request your emissions data directly on {{brandName}}. Accept a request to share the goods and reporting period they need — no more spreadsheet email chains.',
              { brandName: brand.name },
            )}
          </p>
        </div>
        {connections.length > 0 ? (
          <span className="shrink-0 text-xs font-medium text-primaryColor bg-primaryColor/10 rounded-xl px-3 py-1.5">
            {t('freeSupplier:dataRequests.incomingCount', 'Incoming ({{count}})', {
              count: connections.length,
            })}
          </span>
        ) : null}
      </div>

      {inbox.isPending ? (
        // One ConnectionPanel per declarant, each with its request rows — the
        // skeleton stands in for that same shape so the page doesn't jump from
        // a one-line placeholder to full-height panels.
        <SupplierCardListSkeleton
          cards={2}
          rowsPerCard={2}
        />
      ) : inbox.isError ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center">
          <p className="text-sm text-red-700 m-0">
            {t('freeSupplier:dataRequests.loadError', 'We couldn’t load your requests.')}
          </p>
          <CgRadixButton
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => inbox.refetch()}
          >
            {t('freeSupplier:dataRequests.tryAgain', 'Try again')}
          </CgRadixButton>
        </div>
      ) : connections.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 p-10 text-center">
          <FontAwesomeIcon
            icon={faInbox}
            className="text-slate-300 text-2xl mb-3"
          />
          <p className="text-sm text-slate-500 m-0">
            {t('freeSupplier:dataRequests.emptyState.title', 'No requests yet.')}
          </p>
          <p className="text-xs text-slate-400 mt-1 m-0 max-w-sm mx-auto">
            {t(
              'freeSupplier:dataRequests.emptyState.description',
              'When a declarant connects and asks for your data, it appears here — with the exact CN codes and reporting period they need.',
            )}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {connections.map((connection) => (
            <ConnectionPanel
              key={connection.id}
              connection={connection}
              actions={actions}
              busy={busy}
              onProvideData={onProvideData}
            />
          ))}
        </div>
      )}

      {/* Item 20: "Provide data" opens this IMMEDIATELY — no intermediate
          page card (the old bottom-of-page fallback, easy to miss, is
          deleted along with its unresolvable-installation branch). Request
          context/progress live inside the modal via contextNotice; the
          target installation is an explicit, required, always-empty pick
          the supplier makes on every line via installationChoice (never a
          resolution/heuristic — a request only names the product). Re-
          mounted (via `key`) whenever the walk advances, so
          InstallationRealEmissionsModal's own open-sync effect re-seeds
          from scratch instead of carrying stale form/installation state
          from the previous line into the next. The modal is not mounted
          until the CN catalog is ready (brief overlay meanwhile): mounting
          earlier would freeze an unresolved CN seed into the store, and
          remounting on readiness instead would silently discard anything
          the supplier had already entered. closeOnSuccess is always
          false: a successful save (even the LAST one) never lets the shared
          modal auto-close itself — advanceProvideDataFlow clears
          provideDataFlow once its own invalidate/notify finishes, which
          simply stops rendering (and so unmounts) this modal. Only an
          explicit Close/X ever calls closeProvideDataFlow directly, which
          abandons the rest of the queue cleanly (nothing left half-open). */}
      {provideDataFlow && !cnGoodsReady ? (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40"
          role="status"
          aria-label={
            cnGoodsQuery.isError
              ? t(
                  'freeSupplier:dataRequests.entryOverlay.unavailableAriaLabel',
                  'Data entry unavailable',
                )
              : t(
                  'freeSupplier:dataRequests.entryOverlay.preparingAriaLabel',
                  'Preparing data entry',
                )
          }
        >
          <div className="rounded-lg bg-white px-6 py-4 text-sm text-slate-600 shadow-lg">
            {cnGoodsQuery.isError ? (
              <div className="flex flex-col items-center gap-3">
                <span>
                  {t(
                    'freeSupplier:dataRequests.entryOverlay.catalogError',
                    'We couldn’t load the product catalog needed for data entry.',
                  )}
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="rounded-md bg-slate-800 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-700"
                    onClick={() => cnGoodsQuery.refetch()}
                  >
                    {t('freeSupplier:dataRequests.entryOverlay.retry', 'Retry')}
                  </button>
                  <button
                    type="button"
                    className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                    onClick={closeProvideDataFlow}
                  >
                    {t('freeSupplier:dataRequests.entryOverlay.close', 'Close')}
                  </button>
                </div>
              </div>
            ) : (
              t('freeSupplier:dataRequests.entryOverlay.preparing', 'Preparing data entry…')
            )}
          </div>
        </div>
      ) : null}
      {provideDataFlow && cnGoodsReady ? (
        <SupplierEmissionsModal
          key={`${provideDataFlow.requestId}-${currentEntry.index}`}
          show
          onClose={closeProvideDataFlow}
          operator={operator}
          installationChoice={{ installations, required: true }}
          selectedEmission={seed}
          contextNotice={contextNotice}
          handleRefresh={({ mode } = {}) => advanceProvideDataFlow(mode)}
          defaultSaveMode={SupplierSaveMode.Publish}
          closeOnSuccess={false}
          onSkip={() => advanceProvideDataFlow('skip')}
        />
      ) : null}
    </div>
  );
}
